/**
 * Caché en localStorage de la lista completa de definiciones de modelo.
 *
 * `GET /api/gotrading/model-definitions` (getAll) devuelve los esquemas de todos los
 * modelos del tenant, ~420 KB. No son datos: las filas viven en
 * /api/gotrading/dynamic/<modelo> y no pasan por acá.
 *
 * La pantalla de listado la pide tres veces por carga, y las tres salen a la vez.
 * Aislada tarda ~135 ms; en medio de la carga tardaba 1.700-2.400 ms, porque compite
 * con las otras ~40 llamadas sobre un servidor de 2 vCPU. Por eso hay dos mecanismos
 * acá, y sirven para cosas distintas:
 *
 *  - Deduplicación en vuelo: si varias piden lo mismo antes de que ninguna haya
 *    respondido, sale una sola. No tiene riesgo de datos viejos, porque es la misma
 *    petición en el mismo instante.
 *  - Caché en localStorage: evita la llamada en cargas posteriores.
 *
 * Por qué la clave incluye al usuario: getAll ejecuta el hook `defaultvalues` de cada
 * modelo (modelDefinitionController.getAll), y ese hook puede meter valores que
 * dependen de quién pregunta — hoy, en fobtrading, el nombre del usuario conectado
 * como default de `negotiation.name`. Sin el usuario en la clave, en un navegador
 * compartido un usuario vería los defaults del anterior.
 *
 * NO se cachea `/model-definitions/<modelo>` (getOne) por lo mismo, y porque ahí el
 * hook tiene más superficie. El grueso de la ganancia estaba en getAll de todos modos.
 *
 * Invalidación: versión de deploy del backend (cubre migraciones y el modelo `User`,
 * que se genera desde el esquema de Mongoose), write-through al editar una definición,
 * y edad máxima de 12 h como respaldo.
 */

const PREFIX = "mdc:";
const VERSION_KEY = `${PREFIX}__version`;
const ALL_PREFIX = `${PREFIX}all:`;
const MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12 h
// Último recurso: al vencer, las que esperaban salen a la red por su cuenta.
const INFLIGHT_TIMEOUT_MS = 10000;

/** Peticiones en vuelo, por clave. */
const inflight = new Map();

/** localStorage puede tirar (modo privado, cuota, políticas del navegador). */
function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    // Cuota llena: soltamos lo nuestro y reintentamos una vez.
    clearModelDefinitionCache();
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e2) {
      return false;
    }
  }
}

function safeRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // ignorar
  }
}

/** Usuario actual. Sin sesión no se cachea nada. */
function currentUserId() {
  const userId = safeGet("userId");
  return userId && String(userId).trim() ? String(userId).trim() : null;
}

function entryKey() {
  const userId = currentUserId();
  return userId ? `${ALL_PREFIX}${userId}` : null;
}

/** Claves del caché presentes hoy (sin la de versión). */
function cacheKeys() {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PREFIX) && key !== VERSION_KEY) {
        keys.push(key);
      }
    }
  } catch (e) {
    // ignorar
  }
  return keys;
}

/** Borra todas las entradas del caché (no toca el resto de localStorage). */
export function clearModelDefinitionCache() {
  cacheKeys().forEach((key) => safeRemove(key));
}

/** Definición cacheada, o null si no hay, si venció o si es de otra versión de deploy. */
export function readCachedModelDefinitions() {
  const key = entryKey();
  if (!key) return null;

  const raw = safeGet(key);
  if (!raw) return null;

  let entry;
  try {
    entry = JSON.parse(raw);
  } catch (e) {
    safeRemove(key);
    return null;
  }

  if (!entry || entry.data === undefined) return null;

  // La versión de deploy vigente manda: si no coincide, la entrada es de antes.
  const currentVersion = safeGet(VERSION_KEY);
  if (currentVersion && entry.version !== currentVersion) {
    safeRemove(key);
    return null;
  }

  if (!entry.storedAt || Date.now() - entry.storedAt > MAX_AGE_MS) {
    safeRemove(key);
    return null;
  }

  return entry.data;
}

export function writeCachedModelDefinitions(data) {
  const key = entryKey();
  if (!key || data === undefined || data === null) return;

  // Un navegador compartido no necesita guardar los 420 KB de cada usuario que pasó.
  cacheKeys().forEach((other) => {
    if (other !== key) safeRemove(other);
  });

  const entry = {
    data,
    version: safeGet(VERSION_KEY) || null,
    storedAt: Date.now(),
  };

  try {
    safeSet(key, JSON.stringify(entry));
  } catch (e) {
    // No serializable: no cachear.
  }
}

/**
 * Definición de un modelo derivada de la lista completa.
 *
 * Comprobado contra el API: `GET /model-definitions/<modelo>` devuelve exactamente la
 * misma entrada que trae `getAll`, más una clave `redirect`. Y `redirect` solo puede
 * valer `true` en las ramas del hook que exigen `parentModel`, que llega por query
 * string — o sea, nunca en la forma simple. Así que la lista alcanza.
 *
 * Los modelos de grupo (`Padre_campo`) quedan fuera: `getOne` los arma con un camino
 * propio y no comprobé que coincida con lo que mete `getAll`.
 */
export function deriveModelDefinitionFromList(list, modelName) {
  if (!Array.isArray(list) || !modelName) return null;
  if (String(modelName).includes("_")) return null;

  const found = list.find((m) => m && m.modelName === modelName);
  if (!found) return null;

  return { ...found, redirect: false };
}

/** Igual que la anterior, pero leyendo la lista del caché. */
export function findInCachedModelDefinitions(modelName) {
  return deriveModelDefinitionFromList(readCachedModelDefinitions(), modelName);
}

/**
 * Nombre del modelo si la URL es la forma simple `/model-definitions/<modelo>`.
 *
 * Con query string devuelve null: `?parentID=…` hace que el hook genere valores
 * distintos (incluidos números de autoincremento), y `/id/<objectId>` resuelve por id.
 */
export function getModelDefinitionName(url) {
  if (!url) return null;

  const raw = String(url);
  if (raw.includes("?")) return null;

  const base = "/api/gotrading/model-definitions";
  const at = raw.indexOf(base);
  if (at === -1) return null;

  const match = raw.slice(at + base.length).match(/^\/([^/]+)$/);
  if (!match) return null;

  const name = decodeURIComponent(match[1]);
  return name && name !== "id" ? name : null;
}

/** Promesa de la petición ya en vuelo, si la hay. */
export function getInflightModelDefinitions() {
  const pending = inflight.get(ALL_PREFIX);
  return pending ? pending.promise : null;
}

/**
 * Marca esta petición como la que sale a la red. Las siguientes esperan su resultado
 * en vez de disparar otra.
 */
export function markInflightModelDefinitions() {
  if (inflight.has(ALL_PREFIX)) return;

  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // Sin esto, un fallo deja un rechazo sin manejar en consola.
  promise.catch(() => {});

  // Red de seguridad: si la petición nunca se resuelve (pestaña suspendida, request
  // cancelado), las que esperan no pueden quedar colgadas.
  const timer = setTimeout(() => {
    settleInflightModelDefinitions(
      null,
      new Error("model-definitions timeout")
    );
  }, INFLIGHT_TIMEOUT_MS);

  inflight.set(ALL_PREFIX, { promise, resolve, reject, timer });
}

export function settleInflightModelDefinitions(data, error) {
  const pending = inflight.get(ALL_PREFIX);
  if (!pending) return;

  inflight.delete(ALL_PREFIX);
  clearTimeout(pending.timer);
  if (error) {
    pending.reject(error);
  } else {
    pending.resolve(data);
  }
}

/**
 * Alinea el caché con la versión desplegada del backend.
 *
 * Se llama al arrancar y NO bloquea: las lecturas usan lo que haya. Si el backend
 * cambió, se limpia todo y la próxima navegación trae definiciones frescas.
 */
export async function syncModelDefinitionCacheVersion() {
  try {
    const { wencesApi } = await import("@/api/wencesApi");
    const { data } = await wencesApi.get("/api/deploy-version");

    const version = data?.gitCommit || data?.deployedAt || null;
    if (!version) return;

    const stored = safeGet(VERSION_KEY);
    if (stored !== version) {
      clearModelDefinitionCache();
      safeSet(VERSION_KEY, version);
    }
  } catch (e) {
    // Sin versión seguimos con la edad máxima como respaldo.
  }
}

/**
 * ¿Es esta URL la lista completa de definiciones?
 *
 * Solo la forma exacta `/api/gotrading/model-definitions`. Cualquier cosa con query
 * string queda fuera: `?raw=true` devuelve las definiciones sin filtrar por
 * clientVisibility, y el hook `defaultvalues` lee `req.query`.
 */
export function isModelDefinitionsListUrl(url) {
  if (!url) return false;

  const raw = String(url);
  if (raw.includes("?")) return false;

  const base = "/api/gotrading/model-definitions";
  const at = raw.indexOf(base);
  if (at === -1) return false;

  const rest = raw.slice(at + base.length);
  return rest === "" || rest === "/";
}
